import React, { useMemo, useState } from "react";

interface OptionChainRow {
  strike: number;
  callOI: number;
  callVolume: number;
  callLTP: number;
  callIV: string;
  callBid: number;
  callAsk: number;
  putOI: number;
  putVolume: number;
  putLTP: number;
  putIV: string;
  putBid: number;
  putAsk: number;
}

interface OIChangeData {
  strike: number;
  callOIChange: number;
  putOIChange: number;
}

interface OIChartProps {
  optionChain: OptionChainRow[];
  spotPrice: number;
  height?: number;
  oiChangeData?: OIChangeData[];
  pcrTrend?: { time: string; pcr: number }[];
}

type ViewMode = "oi" | "volume" | "change" | "heatmap";

const OIChart: React.FC<OIChartProps> = ({
  optionChain,
  spotPrice,
  height = 350,
  oiChangeData = [],
  pcrTrend = [],
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("oi");
  const [hoveredStrike, setHoveredStrike] = useState<number | null>(null);

  // Build OI change map for quick lookup
  const oiChangeMap = useMemo(() => {
    const map = new Map<number, OIChangeData>();
    oiChangeData.forEach(d => map.set(d.strike, d));
    return map;
  }, [oiChangeData]);

  const chartData = useMemo(() => {
    if (!optionChain.length) return { rows: [], maxOI: 0, totalCallOI: 0, totalPutOI: 0, totalCallChange: 0, totalPutChange: 0 };

    const sorted = [...optionChain].sort((a, b) => a.strike - b.strike);
    const atmIndex = sorted.findIndex((r) => r.strike >= spotPrice);
    const start = Math.max(0, atmIndex - 15);
    const end = Math.min(sorted.length, atmIndex + 16);
    const filtered = sorted.slice(start, end);

    let maxVal = 0;
    let totalCallOI = 0;
    let totalPutOI = 0;
    let totalCallChange = 0;
    let totalPutChange = 0;

    const rows = filtered.map((row) => {
      const change = oiChangeMap.get(row.strike);
      const callChange = change?.callOIChange || 0;
      const putChange = change?.putOIChange || 0;

      let callVal: number, putVal: number;
      if (viewMode === "volume") {
        callVal = row.callVolume;
        putVal = row.putVolume;
      } else if (viewMode === "change") {
        callVal = Math.abs(callChange);
        putVal = Math.abs(putChange);
      } else {
        callVal = row.callOI;
        putVal = row.putOI;
      }

      totalCallOI += row.callOI;
      totalPutOI += row.putOI;
      totalCallChange += callChange;
      totalPutChange += putChange;
      maxVal = Math.max(maxVal, callVal, putVal);

      return { ...row, callVal, putVal, callChange, putChange };
    });

    return { rows, maxOI: maxVal, totalCallOI, totalPutOI, totalCallChange, totalPutChange };
  }, [optionChain, spotPrice, viewMode, oiChangeMap]);

  const formatOI = (val: number): string => {
    if (val >= 10000000) return (val / 10000000).toFixed(1) + "Cr";
    if (val >= 100000) return (val / 100000).toFixed(1) + "L";
    if (val >= 1000) return (val / 1000).toFixed(1) + "K";
    return val.toString();
  };

  const formatChange = (val: number): string => {
    const prefix = val > 0 ? "+" : "";
    return prefix + formatOI(val);
  };

  const pcr = chartData.totalCallOI > 0
    ? (chartData.totalPutOI / chartData.totalCallOI).toFixed(2)
    : "0.00";

  if (!optionChain.length) {
    return (
      <div className="box py-4 has-text-centered has-text-grey">
        <p>No option chain data available</p>
      </div>
    );
  }

  return (
    <div
      className="box py-2 px-2"
      style={{ backgroundColor: "#1e222d", borderRadius: "4px" }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0.25rem 0.5rem",
          marginBottom: "0.25rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span style={{ color: "#d1d4dc", fontWeight: 600, fontSize: "0.8rem" }}>
            {viewMode === "change" ? "OI Change" : "Put / Call OI"}
          </span>
          <span style={{ fontSize: "0.7rem", color: "#26a69a" }}>
            {viewMode === "change"
              ? `Call Chg: ${formatChange(chartData.totalCallChange)}`
              : `Call OI: ${formatOI(chartData.totalCallOI)}`}
          </span>
          <span style={{ fontSize: "0.7rem", color: "#ef5350" }}>
            {viewMode === "change"
              ? `Put Chg: ${formatChange(chartData.totalPutChange)}`
              : `Put OI: ${formatOI(chartData.totalPutOI)}`}
          </span>
          <span
            style={{
              fontSize: "0.7rem",
              color:
                parseFloat(pcr) > 1 ? "#26a69a" : parseFloat(pcr) < 0.7 ? "#ef5350" : "#ffd54f",
            }}
          >
            PCR: {pcr}
          </span>
        </div>
        <div style={{ display: "flex", gap: "0.25rem" }}>
          {(["oi", "volume", "change", "heatmap"] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: "0.15rem 0.5rem",
                fontSize: "0.65rem",
                border: "1px solid #363a45",
                borderRadius: "3px",
                cursor: "pointer",
                backgroundColor: viewMode === mode ? "#2962ff" : "transparent",
                color: viewMode === mode ? "#fff" : "#787b86",
              }}
            >
              {mode === "oi" ? "OI" : mode === "volume" ? "Vol" : mode === "change" ? "Chg" : "🌡 Heat"}
            </button>
          ))}
        </div>
      </div>

      {/* Change mode: no data warning */}
      {viewMode === "change" && oiChangeData.length === 0 && (
        <div style={{ padding: "0.5rem", textAlign: "center", color: "#787b86", fontSize: "0.7rem" }}>
          OI change data requires at least 2 snapshots (enable live mode for 10+ min)
        </div>
      )}

      {/* Heatmap View */}
      {viewMode === "heatmap" && (
        <div style={{ maxHeight: height + "px", overflowY: "auto", padding: "0.25rem 0.5rem" }}>
          {chartData.rows.map((row) => {
            const maxOI = chartData.maxOI || 1;
            const ceIntensity = Math.min(1, row.callOI / maxOI);
            const peIntensity = Math.min(1, row.putOI / maxOI);
            const isATM = Math.abs(row.strike - spotPrice) <= (optionChain[1]?.strike - optionChain[0]?.strike || 50) / 2;
            const ceAlpha = (ceIntensity * 0.85 + 0.05).toFixed(2);
            const peAlpha = (peIntensity * 0.85 + 0.05).toFixed(2);
            return (
              <div key={row.strike} style={{ display: "flex", alignItems: "stretch", gap: 2, marginBottom: 2, height: 22 }}>
                {/* CE bar */}
                <div style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 6,
                  background: `rgba(239,83,80,${ceAlpha})`, borderRadius: "3px 0 0 3px",
                  transition: "background 0.3s",
                }}>
                  <span style={{ fontSize: "0.6rem", color: "#fff", fontFamily: "monospace" }}>{formatOI(row.callOI)}</span>
                </div>
                {/* Strike label */}
                <div style={{
                  width: 56, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  background: isATM ? "#2962ff" : "#1e222d",
                  borderTop: isATM ? "1px solid #2962ff" : "none",
                  borderBottom: isATM ? "1px solid #2962ff" : "none",
                }}>
                  <span style={{ fontSize: "0.62rem", fontWeight: isATM ? 700 : 500, color: "#d1d4dc", fontFamily: "monospace" }}>{row.strike}</span>
                </div>
                {/* PE bar */}
                <div style={{
                  flex: 1, display: "flex", alignItems: "center", paddingLeft: 6,
                  background: `rgba(38,166,154,${peAlpha})`, borderRadius: "0 3px 3px 0",
                  transition: "background 0.3s",
                }}>
                  <span style={{ fontSize: "0.6rem", color: "#fff", fontFamily: "monospace" }}>{formatOI(row.putOI)}</span>
                </div>
              </div>
            );
          })}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.55rem", color: "#555", marginTop: "0.3rem", padding: "0 2px" }}>
            <span style={{ color: "#ef5350" }}>← CE OI (resistance intensity)</span>
            <span style={{ color: "#26a69a" }}>PE OI (support intensity) →</span>
          </div>
        </div>
      )}

      {/* Chart Body — hidden in heatmap mode */}
      <div
        style={{
          maxHeight: height + "px",
          overflowY: "auto",
          overflowX: "hidden",
          display: viewMode === "heatmap" ? "none" : undefined,
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#1e222d",
                zIndex: 1,
              }}
            >
              <th
                style={{
                  width: "40%",
                  textAlign: "right",
                  color: "#26a69a",
                  fontSize: "0.65rem",
                  padding: "0.2rem 0.5rem",
                  fontWeight: 600,
                }}
              >
                CALL {viewMode === "volume" ? "VOL" : viewMode === "change" ? "CHG" : "OI"}
              </th>
              <th
                style={{
                  width: "20%",
                  textAlign: "center",
                  color: "#d1d4dc",
                  fontSize: "0.65rem",
                  padding: "0.2rem",
                  fontWeight: 600,
                }}
              >
                STRIKE
              </th>
              <th
                style={{
                  width: "40%",
                  textAlign: "left",
                  color: "#ef5350",
                  fontSize: "0.65rem",
                  padding: "0.2rem 0.5rem",
                  fontWeight: 600,
                }}
              >
                PUT {viewMode === "volume" ? "VOL" : viewMode === "change" ? "CHG" : "OI"}
              </th>
            </tr>
          </thead>
          <tbody>
            {chartData.rows.map((row) => {
              const callPct = chartData.maxOI > 0 ? (row.callVal / chartData.maxOI) * 100 : 0;
              const putPct = chartData.maxOI > 0 ? (row.putVal / chartData.maxOI) * 100 : 0;
              const isATM =
                Math.abs(row.strike - spotPrice) <=
                (optionChain[1]?.strike - optionChain[0]?.strike || 50) / 2;
              const isITMCall = row.strike < spotPrice;
              const isHovered = hoveredStrike === row.strike;

              // In change mode, use green for OI buildup (positive), orange for unwinding (negative)
              const callBarColor = viewMode === "change"
                ? (row.callChange >= 0 ? "rgba(38, 166, 154, 0.35)" : "rgba(255, 152, 0, 0.35)")
                : isITMCall ? "rgba(38, 166, 154, 0.12)" : "rgba(38, 166, 154, 0.25)";
              const putBarColor = viewMode === "change"
                ? (row.putChange >= 0 ? "rgba(239, 83, 80, 0.35)" : "rgba(255, 152, 0, 0.35)")
                : !isITMCall ? "rgba(239, 83, 80, 0.12)" : "rgba(239, 83, 80, 0.25)";

              return (
                <tr
                  key={row.strike}
                  onMouseEnter={() => setHoveredStrike(row.strike)}
                  onMouseLeave={() => setHoveredStrike(null)}
                  style={{
                    backgroundColor: isATM
                      ? "rgba(41, 98, 255, 0.15)"
                      : isHovered
                      ? "rgba(255,255,255,0.03)"
                      : "transparent",
                    borderTop: isATM ? "1px solid #2962ff" : "none",
                    borderBottom: isATM ? "1px solid #2962ff" : "none",
                  }}
                >
                  {/* Call Bar */}
                  <td
                    style={{
                      padding: "1px 4px",
                      textAlign: "right",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: callPct + "%",
                        backgroundColor: callBarColor,
                        transition: "width 0.3s ease",
                      }}
                    />
                    <span
                      style={{
                        position: "relative",
                        fontSize: "0.7rem",
                        color: viewMode === "change"
                          ? (row.callChange > 0 ? "#26a69a" : row.callChange < 0 ? "#FF9800" : "#555")
                          : (row.callVal > 0 ? "#26a69a" : "#555"),
                        fontWeight: isATM ? 700 : 400,
                        fontFamily: "monospace",
                      }}
                    >
                      {viewMode === "change"
                        ? (row.callChange !== 0 ? formatChange(row.callChange) : "-")
                        : (row.callVal > 0 ? formatOI(row.callVal) : "-")}
                    </span>
                  </td>

                  {/* Strike Price */}
                  <td style={{ textAlign: "center", padding: "1px 2px" }}>
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: isATM ? 700 : 500,
                        color: isATM ? "#2962ff" : "#d1d4dc",
                        fontFamily: "monospace",
                      }}
                    >
                      {row.strike}
                    </span>
                    {isATM && (
                      <span style={{ fontSize: "0.5rem", color: "#2962ff", display: "block", lineHeight: 1 }}>
                        ATM
                      </span>
                    )}
                  </td>

                  {/* Put Bar */}
                  <td
                    style={{
                      padding: "1px 4px",
                      textAlign: "left",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: putPct + "%",
                        backgroundColor: putBarColor,
                        transition: "width 0.3s ease",
                      }}
                    />
                    <span
                      style={{
                        position: "relative",
                        fontSize: "0.7rem",
                        color: viewMode === "change"
                          ? (row.putChange > 0 ? "#ef5350" : row.putChange < 0 ? "#FF9800" : "#555")
                          : (row.putVal > 0 ? "#ef5350" : "#555"),
                        fontWeight: isATM ? 700 : 400,
                        fontFamily: "monospace",
                      }}
                    >
                      {viewMode === "change"
                        ? (row.putChange !== 0 ? formatChange(row.putChange) : "-")
                        : (row.putVal > 0 ? formatOI(row.putVal) : "-")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tooltip for hovered strike */}
      {hoveredStrike && (
        <div
          style={{
            padding: "0.25rem 0.5rem",
            borderTop: "1px solid #2b2b43",
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.65rem",
            color: "#787b86",
          }}
        >
          {(() => {
            const row = chartData.rows.find((r) => r.strike === hoveredStrike);
            if (!row) return null;
            const change = oiChangeMap.get(row.strike);
            return (
              <>
                <span>Strike: {row.strike}</span>
                <span style={{ color: "#26a69a" }}>
                  Call OI: {formatOI(row.callOI)} | Vol: {formatOI(row.callVolume)} | LTP: {row.callLTP.toFixed(2)}
                  {change ? ` | Chg: ${formatChange(change.callOIChange)}` : ""}
                </span>
                <span style={{ color: "#ef5350" }}>
                  Put OI: {formatOI(row.putOI)} | Vol: {formatOI(row.putVolume)} | LTP: {row.putLTP.toFixed(2)}
                  {change ? ` | Chg: ${formatChange(change.putOIChange)}` : ""}
                </span>
              </>
            );
          })()}
        </div>
      )}

      {/* PCR Trend Mini Chart */}
      {pcrTrend.length > 1 && (
        <div style={{
          borderTop: "1px solid #2b2b43",
          padding: "0.35rem 0.5rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
        }}>
          <span style={{ fontSize: "0.65rem", color: "#787b86", whiteSpace: "nowrap" }}>PCR Trend:</span>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: "24px", flex: 1 }}>
            {pcrTrend.slice(-20).map((pt, i) => {
              const minPCR = Math.min(...pcrTrend.slice(-20).map(p => p.pcr));
              const maxPCR = Math.max(...pcrTrend.slice(-20).map(p => p.pcr));
              const range = maxPCR - minPCR || 1;
              const pct = ((pt.pcr - minPCR) / range) * 100;
              const color = pt.pcr > 1 ? "#26a69a" : pt.pcr < 0.7 ? "#ef5350" : "#ffd54f";
              return (
                <div key={i} style={{
                  flex: 1,
                  height: `${Math.max(4, pct)}%`,
                  backgroundColor: color,
                  borderRadius: "1px",
                  minWidth: "3px",
                }} title={`${pt.time}: PCR ${pt.pcr.toFixed(2)}`} />
              );
            })}
          </div>
          <span style={{ fontSize: "0.65rem", color: "#d1d4dc", whiteSpace: "nowrap" }}>
            {pcrTrend[pcrTrend.length - 1].pcr.toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
};

export default OIChart;
